'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import { toast } from 'sonner';
import dynamic from 'next/dynamic';
import { visitesService, documentsService } from '@/lib/supabase/services';
import type { Visite, Document, Enseigne } from '@/lib/supabase/services';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';


import { computeEnrichedMatchScore } from '@/lib/matching/scoreCalculator';

const TABS_BASE = ['Aperçu', 'Matching', 'Visites', 'Documents'];
const TABS_ADMIN = ['Aperçu', 'Matching', 'Visites', 'Documents', 'Historique'];

const statusColors: Record<string, string> = {
  'Publié': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'En discussion': 'bg-blue-50 text-blue-700 border-blue-200',
  'Offre reçue': 'bg-violet-50 text-violet-700 border-violet-200',
  'En négociation': 'bg-amber-50 text-amber-700 border-amber-200',
  'Visité': 'bg-cyan-50 text-cyan-700 border-cyan-200',
  'Brouillon': 'bg-slate-50 text-slate-600 border-slate-200',
  'En revue': 'bg-orange-50 text-orange-700 border-orange-200',
  'À fort potentiel': 'bg-purple-50 text-purple-700 border-purple-200',
  'Loué / retiré': 'bg-slate-100 text-slate-500 border-slate-200',
  'Réalisée': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Planifiée': 'bg-blue-50 text-blue-700 border-blue-200',
  'Annulée': 'bg-red-50 text-red-700 border-red-200',
  'À reprogrammer': 'bg-amber-50 text-amber-700 border-amber-200',
  'Validé': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'À vérifier': 'bg-amber-50 text-amber-700 border-amber-200',
  'Manquant': 'bg-red-50 text-red-700 border-red-200',
  'Reçu': 'bg-blue-50 text-blue-700 border-blue-200',
  'Expiré': 'bg-slate-50 text-slate-500 border-slate-200',
  'Contacté': 'bg-cyan-50 text-cyan-700 border-cyan-200',
  'À contacter': 'bg-slate-50 text-slate-600 border-slate-200',
};

const docTypeColors: Record<string, string> = {
  'Juridique': 'bg-violet-50 text-violet-600',
  'Technique': 'bg-blue-50 text-blue-600',
  'Médias': 'bg-amber-50 text-amber-600',
  'Administratif': 'bg-slate-50 text-slate-600',
  'Commercial': 'bg-emerald-50 text-emerald-600',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function val(v: number | string | undefined | null): boolean {
  if (v === null || v === undefined) return false;
  if (typeof v === 'number') return v > 0;
  return v.trim().length > 0 && v !== '—';
}

function ScoreRing({ score }: { score: number }) {
  const color = score >= 90 ? '#10b981' : score >= 75 ? '#6366f1' : score >= 50 ? '#f59e0b' : '#ef4444';
  const r = 22;
  const circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  return (
    <div className="relative w-16 h-16 flex items-center justify-center flex-shrink-0">
      <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 56 56">
        <circle cx="28" cy="28" r={r} fill="none" stroke="#f1f5f9" strokeWidth="4" />
        <circle cx="28" cy="28" r={r} fill="none" stroke={color} strokeWidth="4"
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" />
      </svg>
      <span className="text-sm font-700 text-slate-900 relative z-10">{score}</span>
    </div>
  );
}

function FeatureBadge({ label, active }: { label: string; active: boolean }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-600 border ${
      active
        ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :'bg-slate-50 text-slate-400 border-slate-200 line-through'
    }`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${active ? 'bg-emerald-500' : 'bg-slate-300'}`} />
      {label}
    </span>
  );
}

function MatchBadge({ label, compatible }: { label: string; compatible: boolean }) {
  return (
    <div className={`flex items-center justify-between px-4 py-3 rounded-xl border ${
      compatible ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'
    }`}>
      <span className="text-sm font-600 text-slate-800">{label}</span>
      <span className={`text-sm font-700 ${compatible ? 'text-emerald-600' : 'text-red-500'}`}>
        {compatible ? '✅' : '❌'}
      </span>
    </div>
  );
}

// ─── Photo Gallery Modal ──────────────────────────────────────────────────────

function PhotoGalleryModal({ photos, onClose, initialIndex = 0 }: { photos: string[]; onClose: () => void; initialIndex?: number }) {
  const [current, setCurrent] = useState(initialIndex);

  const prev = useCallback(() => setCurrent(i => (i - 1 + photos.length) % photos.length), [photos.length]);
  const next = useCallback(() => setCurrent(i => (i + 1) % photos.length), [photos.length]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, prev, next]);

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex flex-col items-center justify-center p-4 animate-fade-in" onClick={onClose}>
      <div className="relative w-full max-w-5xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <span className="text-white/70 text-sm font-500">{current + 1} / {photos.length}</span>
          <button onClick={onClose} className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {/* Main image */}
        <div className="relative rounded-2xl overflow-hidden bg-black/50" style={{ maxHeight: '70vh' }}>
          <img
            src={photos[current]}
            alt={`Photo ${current + 1}`}
            loading="eager"
            decoding="async"
            className="w-full h-full object-contain"
            style={{ maxHeight: '70vh' }}
          />
          {photos.length > 1 && (
            <>
              <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 p-3 rounded-xl bg-black/50 hover:bg-black/70 text-white transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 p-3 rounded-xl bg-black/50 hover:bg-black/70 text-white transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}
        </div>
        {/* Thumbnails */}
        {photos.length > 1 && (
          <div className="flex gap-2 mt-4 overflow-x-auto pb-1 justify-center">
            {photos.map((url, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className={`flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                  i === current ? 'border-white opacity-100' : 'border-transparent opacity-50 hover:opacity-80'
                }`}
              >
                <img src={url} alt={`Miniature ${i + 1}`} loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Matching helpers ─────────────────────────────────────────────────────────

function computeMatchScore(local: any, enseigne: Enseigne): number {
  return computeEnrichedMatchScore(local, enseigne).scoreGlobal;
}

// ─── Main Component ───────────────────────────────────────────────────────────

const MapView = dynamic(() => import('@/components/ui/MapView'), { ssr: false });

// ─── PDF Teaser Generator ─────────────────────────────────────────────────────

async function generatePDFTeaser(p: any, photos: string[]) {
  const { jsPDF } = await import('jspdf');

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const W = 210;
  const H = 297;
  const margin = 14;
  const contentW = W - margin * 2;

  // ── Helpers ──────────────────────────────────────────────────────────────
  const fmt = (n: number) => n > 0 ? n.toLocaleString('fr-FR') : '—';
  const fmtStr = (s: string) => s && s !== 'Inconnue' ? s : '—';

  // ── Colors ───────────────────────────────────────────────────────────────
  const DARK = [15, 23, 42] as [number, number, number];
  const AMBER = [245, 158, 11] as [number, number, number];
  const SLATE = [100, 116, 139] as [number, number, number];
  const LIGHT = [241, 245, 249] as [number, number, number];
  const WHITE = [255, 255, 255] as [number, number, number];
  const GREEN = [16, 185, 129] as [number, number, number];
  const BORDER = [226, 232, 240] as [number, number, number];
  const TEXT_DARK = [15, 23, 42] as [number, number, number];
  const TEXT_MID = [71, 85, 105] as [number, number, number];

  let y = 0;

  // ── HEADER ───────────────────────────────────────────────────────────────
  doc.setFillColor(...DARK);
  doc.rect(0, 0, W, 18, 'F');

  // Logo box
  doc.setFillColor(...AMBER);
  doc.roundedRect(margin, 4, 10, 10, 2, 2, 'F');
  doc.setFontSize(7);
  doc.setTextColor(...DARK);
  doc.setFont('helvetica', 'bold');
  doc.text('A', margin + 5, 10.5, { align: 'center' });

  // Brand name
  doc.setTextColor(...WHITE);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Axxam Retail', margin + 13, 9);
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...SLATE);
  doc.text('COMMERCIAL REAL ESTATE', margin + 13, 13.5);

  // Ref
  doc.setFontSize(7);
  doc.setTextColor(...SLATE);
  doc.setFont('helvetica', 'normal');
  doc.text('RÉFÉRENCE', W - margin, 8, { align: 'right' });
  doc.setFontSize(9);
  doc.setTextColor(...AMBER);
  doc.setFont('helvetica', 'bold');
  doc.text(p.ref || '—', W - margin, 13.5, { align: 'right' });

  y = 18;

  // ── HERO PHOTO ───────────────────────────────────────────────────────────
  const photoH = 62;
  if (photos.length > 0) {
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise<void>((resolve) => {
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = photos[0];
      });
      if (img.naturalWidth > 0) {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
          doc.addImage(dataUrl, 'JPEG', 0, y, W, photoH);
        }
      }
    } catch {
      // fallback: dark bg
      doc.setFillColor(30, 41, 59);
      doc.rect(0, y, W, photoH, 'F');
    }
  } else {
    doc.setFillColor(30, 41, 59);
    doc.rect(0, y, W, photoH, 'F');
  }

  // Gradient overlay on photo (dark bottom)
  for (let i = 0; i < 28; i++) {
    const alpha = i / 28;
    doc.setFillColor(0, 0, 0);
    doc.setGState(doc.GState({ opacity: alpha * 0.75 }));
    doc.rect(0, y + photoH - 28 + i, W, 1, 'F');
  }
  doc.setGState(doc.GState({ opacity: 1 }));

  // Badges on photo
  let bx = margin;
  const by = y + photoH - 22;
  const badges = [
    p.statut ? { text: p.statut, bg: [16, 185, 129] as [number, number, number] } : null,
    p.offre ? { text: p.offre, bg: [245, 158, 11] as [number, number, number] } : null,
    p.typeLocal ? { text: p.typeLocal, bg: [99, 102, 241] as [number, number, number] } : null,
  ].filter(Boolean) as { text: string; bg: [number, number, number] }[];

  for (const badge of badges) {
    const tw = doc.getStringUnitWidth(badge.text) * 7 / doc.internal.scaleFactor;
    const bw = tw + 6;
    doc.setFillColor(...badge.bg);
    doc.setGState(doc.GState({ opacity: 0.85 }));
    doc.roundedRect(bx, by, bw, 5, 1, 1, 'F');
    doc.setGState(doc.GState({ opacity: 1 }));
    doc.setFontSize(6.5);
    doc.setTextColor(...WHITE);
    doc.setFont('helvetica', 'bold');
    doc.text(badge.text, bx + bw / 2, by + 3.5, { align: 'center' });
    bx += bw + 3;
  }

  // Title on photo
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...WHITE);
  const titleLines = doc.splitTextToSize(p.titre || '—', contentW);
  doc.text(titleLines[0], margin, y + photoH - 10);

  // Address on photo
  const addrParts = [p.adresse, p.quartier, p.ville].filter(Boolean);
  const addrStr = addrParts.length > 0 ? addrParts.join(', ') : '—';
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(203, 213, 225);
  doc.text('📍 ' + addrStr, margin, y + photoH - 4);

  y += photoH;

  // ── KPI ROW ──────────────────────────────────────────────────────────────
  const kpiH = 22;
  doc.setFillColor(...WHITE);
  doc.rect(0, y, W, kpiH, 'F');
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.3);
  doc.line(0, y + kpiH, W, y + kpiH);

  const kpis = [
    { label: 'SURFACE TOTALE', value: p.surfaceTotal > 0 ? fmt(p.surfaceTotal) : '—', unit: p.surfaceTotal > 0 ? 'm²' : '' },
    { label: 'LOYER MENSUEL', value: p.loyer > 0 ? fmt(p.loyer) : 'Sur demande', unit: p.loyer > 0 ? 'MAD/mois' : '' },
    { label: 'FAÇADE', value: p.facade > 0 ? `${p.facade}` : '—', unit: p.facade > 0 ? 'ml' : '' },
    { label: 'DISPONIBILITÉ', value: fmtStr(p.disponibilite), unit: '' },
  ];

  const kpiW = W / 4;
  kpis.forEach((kpi, i) => {
    const kx = i * kpiW;
    if (i > 0) {
      doc.setDrawColor(...BORDER);
      doc.setLineWidth(0.3);
      doc.line(kx, y + 3, kx, y + kpiH - 3);
    }
    doc.setFontSize(6);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...SLATE);
    doc.text(kpi.label, kx + kpiW / 2, y + 6, { align: 'center' });

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...TEXT_DARK);
    doc.text(kpi.value, kx + kpiW / 2, y + 14, { align: 'center' });

    if (kpi.unit) {
      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...SLATE);
      doc.text(kpi.unit, kx + kpiW / 2, y + 19, { align: 'center' });
    }
  });

  y += kpiH;

  // ── TWO-COLUMN BODY ───────────────────────────────────────────────────────
  const colW = contentW / 2 - 3;
  const colGap = 6;
  const col1x = margin;
  const col2x = margin + colW + colGap;
  let y1 = y + 8;
  let y2 = y + 8;

  // ── Section helper ────────────────────────────────────────────────────────
  const drawSectionTitle = (title: string, cx: number, cy: number): number => {
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...SLATE);
    doc.text(title.toUpperCase(), cx, cy);
    doc.setDrawColor(...AMBER);
    doc.setLineWidth(0.8);
    doc.line(cx, cy + 1.5, cx + colW, cy + 1.5);
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.2);
    return cy + 7;
  };

  const drawRow = (label: string, value: string, cx: number, cy: number, highlight = false): number => {
    doc.setDrawColor(...LIGHT);
    doc.setLineWidth(0.2);
    doc.line(cx, cy + 4.5, cx + colW, cy + 4.5);

    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...SLATE);
    doc.text(label, cx, cy + 3);

    doc.setFont('helvetica', highlight ? 'bold' : 'normal');
    doc.setTextColor(highlight ? TEXT_DARK[0] : TEXT_MID[0], highlight ? TEXT_DARK[1] : TEXT_MID[1], highlight ? TEXT_DARK[2] : TEXT_MID[2]);
    doc.setFontSize(highlight ? 8.5 : 7.5);
    const valLines = doc.splitTextToSize(value, colW * 0.52);
    doc.text(valLines[0], cx + colW, cy + 3, { align: 'right' });
    return cy + 6;
  };

  // ── LEFT COLUMN: Détails du bien ──────────────────────────────────────────
  y1 = drawSectionTitle('Détails du bien', col1x, y1);

  if (p.typeLocal) y1 = drawRow('Type de bien', p.typeLocal, col1x, y1);
  if (p.type) y1 = drawRow("Type d'emplacement", p.type, col1x, y1);
  if (p.etatLocal) y1 = drawRow('État du local', p.etatLocal, col1x, y1);
  if (p.hauteurPlafond > 0) y1 = drawRow('Hauteur plafond', `${p.hauteurPlafond} m`, col1x, y1);
  if (p.nbVitrines > 0) y1 = drawRow('Nb vitrines', `${p.nbVitrines}`, col1x, y1);
  if (p.lineaireVitrine > 0) y1 = drawRow('Linéaire vitrine', `${p.lineaireVitrine} ml`, col1x, y1);
  if (p.parking) y1 = drawRow('Parking', `${p.parking} places`, col1x, y1);
  if (p.zoneCommerciale) y1 = drawRow('Zone commerciale', p.zoneCommerciale, col1x, y1);
  if (p.usageAutorise) y1 = drawRow('Usage autorisé', p.usageAutorise, col1x, y1);

  // Surfaces breakdown
  const surfaceRows = [
    { label: 'Surface RDC', value: p.surfaceRDC },
    { label: 'Mezzanine', value: p.surfaceMezzanine },
    { label: 'Sous-sol', value: p.surfaceSousSol },
    { label: 'Étage', value: p.surfaceEtage },
    { label: 'Terrasse', value: p.surfaceTerrasse },
  ].filter(s => s.value > 0);

  if (surfaceRows.length > 0) {
    y1 += 3;
    y1 = drawSectionTitle('Répartition des surfaces', col1x, y1);
    for (const s of surfaceRows) {
      y1 = drawRow(s.label, `${fmt(s.value)} m²`, col1x, y1);
    }
    if (p.surfaceTotal > 0) {
      y1 = drawRow('TOTAL', `${fmt(p.surfaceTotal)} m²`, col1x, y1, true);
    }
  }

  // Équipements
  const equips = [
    p.climatisation && 'Climatisation',
    p.extraction && 'Extraction',
    p.accessibilite && 'Accès PMR',
    p.livraison && 'Accès livraison',
  ].filter(Boolean) as string[];

  if (equips.length > 0) {
    y1 += 3;
    y1 = drawSectionTitle('Équipements', col1x, y1);
    const cols2 = 2;
    const eqW = colW / cols2;
    equips.forEach((eq, i) => {
      const ex = col1x + (i % cols2) * eqW;
      const ey = y1 + Math.floor(i / cols2) * 6;
      doc.setFillColor(...GREEN);
      doc.circle(ex + 1.5, ey - 0.5, 1.2, 'F');
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...TEXT_MID);
      doc.text(eq, ex + 4.5, ey + 0.5);
    });
    y1 += Math.ceil(equips.length / cols2) * 6 + 2;
  }

  // ── RIGHT COLUMN: Conditions commerciales ─────────────────────────────────
  y2 = drawSectionTitle('Conditions commerciales', col2x, y2);

  if (p.loyer > 0) y2 = drawRow('Loyer mensuel', `${fmt(p.loyer)} MAD`, col2x, y2, true);
  if (p.loyer > 0 && p.surfaceTotal > 0) y2 = drawRow('Loyer / m²', `${Math.round(p.loyer / p.surfaceTotal)} MAD/m²`, col2x, y2);
  if (p.pasDePorte > 0) y2 = drawRow('Pas-de-porte', `${fmt(p.pasDePorte)} MAD`, col2x, y2);
  if (p.droitEntree > 0) y2 = drawRow("Droit d'entrée", `${fmt(p.droitEntree)} MAD`, col2x, y2);
  if (p.depotGarantie > 0) y2 = drawRow('Dépôt de garantie', `${fmt(p.depotGarantie)} MAD`, col2x, y2);
  y2 = drawRow('Exclusivité', p.exclusivite ? 'Oui' : 'Non', col2x, y2);
  y2 = drawRow('Disponibilité', fmtStr(p.disponibilite), col2x, y2);

  // Adresse complète
  y2 += 3;
  y2 = drawSectionTitle('Localisation', col2x, y2);
  if (p.adresse) y2 = drawRow('Adresse', p.adresse, col2x, y2);
  if (p.quartier) y2 = drawRow('Quartier', p.quartier, col2x, y2);
  if (p.ville) y2 = drawRow('Ville', p.ville, col2x, y2);
  if (p.zoneCommerciale) y2 = drawRow('Zone commerciale', p.zoneCommerciale, col2x, y2);

  // Environnement
  const envRows = [
    p.fluxPieton && { label: 'Flux piéton', value: p.fluxPieton },
    p.visibiliteEnseigne && { label: 'Visibilité enseigne', value: p.visibiliteEnseigne },
    p.voisinage && { label: 'Voisinage', value: p.voisinage },
    p.proximiteTransports && { label: 'Transports', value: p.proximiteTransports },
  ].filter(Boolean) as { label: string; value: string }[];

  if (envRows.length > 0) {
    y2 += 3;
    y2 = drawSectionTitle('Environnement', col2x, y2);
    for (const row of envRows) {
      y2 = drawRow(row.label, row.value, col2x, y2);
    }
  }

  // Description
  if (p.commentairesPublics) {
    const maxY = Math.max(y1, y2) + 4;
    const descY = maxY;
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(...AMBER);
    doc.setLineWidth(0.8);
    doc.line(margin, descY, margin, descY + 20);
    doc.setLineWidth(0.2);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...SLATE);
    doc.text('DESCRIPTION', margin + 4, descY + 4);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...TEXT_MID);
    const descLines = doc.splitTextToSize(p.commentairesPublics, contentW - 4);
    const maxLines = Math.min(descLines.length, 3);
    doc.text(descLines.slice(0, maxLines), margin + 4, descY + 9);
    y = descY + 4 + maxLines * 4 + 4;
  } else {
    y = Math.max(y1, y2) + 4;
  }

  // ── FOOTER ───────────────────────────────────────────────────────────────
  const footerY = H - 18;
  doc.setFillColor(...DARK);
  doc.rect(0, footerY, W, 18, 'F');

  // Broker info
  if (p.broker) {
    doc.setFillColor(...AMBER);
    doc.circle(margin + 5, footerY + 9, 5, 'F');
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...DARK);
    doc.text(p.broker.charAt(0).toUpperCase(), margin + 5, footerY + 11.5, { align: 'center' });

    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...WHITE);
    doc.text(p.broker, margin + 12, footerY + 8);
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...SLATE);
    doc.text('Broker Axxam Retail', margin + 12, footerY + 13);
  }

  // Contact
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...SLATE);
  doc.text('axxam.cc  •  Maroc', W / 2, footerY + 9, { align: 'center' });

  // Date + disclaimer
  const dateStr = new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  doc.setFontSize(6.5);
  doc.setTextColor(...SLATE);
  doc.text(`Généré le ${dateStr}`, W - margin, footerY + 7, { align: 'right' });
  doc.setFontSize(5.5);
  doc.setTextColor(71, 85, 105);
  doc.text('Document confidentiel — informations non contractuelles', W - margin, footerY + 12, { align: 'right' });

  // ── Save ──────────────────────────────────────────────────────────────────
  const filename = `Axxam_${p.ref || 'local'}_${p.ville || 'teaser'}.pdf`.replace(/\s+/g, '_');
  doc.save(filename);
}

export default function LocalDetailContent({ id }: { id?: string }) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('Aperçu');
  const [property, setProperty] = useState<any | null>(null);
  const [photos, setPhotos] = useState<string[]>([]);
  const [visites, setVisites] = useState<Visite[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [enseignes, setEnseignes] = useState<Enseigne[]>([]);
  const [loading, setLoading] = useState(true);
  const [showGallery, setShowGallery] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [showVisiteModal, setShowVisiteModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showContactBrokerModal, setShowContactBrokerModal] = useState(false);
  const [visiteForm, setVisiteForm] = useState({ enseigne: '', contact: '', date: '', heure: '10:00', note: '' });
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadType, setUploadType] = useState('Juridique');
  const [uploading, setUploading] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ─── Fetch all data on mount ──────────────────────────────────────────────
  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchData() {
      setLoading(true);
      const supabase = createClient();

      // Fetch property (full row)
      const { data: propData, error: propError } = await supabase
        .from('locaux')
        .select(`
          *,
          broker_profile:user_profiles!locaux_broker_id_fkey(id, nom, prenom, email),
          owner_profile:user_profiles!locaux_owner_id_fkey(id, nom, prenom, email)
        `)
        .eq('id', id)
        .maybeSingle();

      if (cancelled) return;

      if (propError) {
        console.error('[LocalDetail] fetch error:', propError.message);
        setLoading(false);
        return;
      }

      if (propData) {
        const buildName = (profile: any) => {
          if (!profile) return '';
          const parts = [profile.prenom, profile.nom].filter(Boolean);
          return parts.length > 0 ? parts.join(' ') : (profile.email || '');
        };
        setProperty({
          ...propData,
          broker: propData.broker_profile ? buildName(propData.broker_profile) : (propData.broker || ''),
          proprietaire: propData.owner_profile ? buildName(propData.owner_profile) : (propData.proprietaire || ''),
        });
      }

      // Fetch photos from storage
      const { data: photoFiles } = await supabase.storage
        .from('local-photos')
        .list(id, { sortBy: { column: 'created_at', order: 'asc' } });

      if (!cancelled && photoFiles && photoFiles.length > 0) {
        const urls = photoFiles.map(file => {
          const { data: urlData } = supabase.storage
            .from('local-photos')
            .getPublicUrl(`${id}/${file.name}`);
          return urlData?.publicUrl || '';
        }).filter(Boolean);
        setPhotos(urls);
      }

      // Fetch visites for this local
      const { data: visitesData } = await supabase
        .from('visites')
        .select(`
          *,
          local:locaux(id, ref, title),
          tenant:user_profiles!visites_tenant_id_fkey(id, nom, prenom, email),
          broker_profile:user_profiles!visites_broker_id_fkey(id, nom, prenom, email)
        `)
        .eq('local_id', id)
        .order('created_at', { ascending: false });

      if (!cancelled && visitesData) {
        const buildName = (profile: any) => {
          if (!profile) return '';
          const parts = [profile.prenom, profile.nom].filter(Boolean);
          return parts.length > 0 ? parts.join(' ') : (profile.email || '');
        };
        setVisites(visitesData.map((row: any) => ({
          id: row.id,
          localId: row.local_id || null,
          local: row.local?.title || '',
          localRef: row.local?.ref || '',
          enseigne: row.tenant ? buildName(row.tenant) : '',
          contact: row.tenant?.email || '',
          date: row.visite_date || '',
          heure: row.heure || '10:00',
          broker: row.broker_profile ? buildName(row.broker_profile) : '',
          brokerId: row.broker_id || null,
          tenantId: row.tenant_id || null,
          statut: (row.statut || 'Planifiée') as Visite['statut'],
          note: row.note || '',
          priorite: (row.priorite || 'Normale') as Visite['priorite'],
        })));
      }

      // Fetch documents for this local
      const { data: docsData } = await supabase
        .from('documents')
        .select('*')
        .eq('local_ref', propData?.ref || '')
        .order('created_at', { ascending: false });

      if (!cancelled && docsData) {
        setDocuments(docsData.map((row: any) => ({
          id: row.id,
          nom: row.nom,
          type: row.type as Document['type'],
          local: row.local,
          localRef: row.local_ref,
          statut: row.statut as Document['statut'],
          uploadedBy: row.uploaded_by || '',
          date: row.date || '',
          taille: row.taille || '',
          version: Number(row.version) || 1,
          permissions: row.permissions || [],
          visibility: (row.visibility || 'broker') as Document['visibility'],
        })));
      }

      // Fetch enseignes for matching tab
      const { data: enseignesData } = await supabase
        .from('enseignes')
        .select('*')
        .order('created_at', { ascending: false });

      if (!cancelled && enseignesData) {
        setEnseignes(enseignesData.map((row: any) => ({
          id: row.id,
          nom: row.nom,
          raisonSociale: row.raison_sociale || '',
          secteur: row.secteur || '',
          statut: row.statut,
          villesCibles: row.villes_cibles || [],
          surfaceMin: Number(row.surface_min) || 0,
          surfaceMax: Number(row.surface_max) || 0,
          budgetMax: Number(row.budget_max) || 0,
          facade: Number(row.facade_min) || 0,
          contact: row.contact || '',
          nbLocaux: Number(row.nb_locaux) || 0,
          score: Number(row.score) || 0,
          updatedAt: row.updated_at || '',
        })));
      }

      if (!cancelled) setLoading(false);
    }

    fetchData();
    return () => { cancelled = true; };
  }, [id]);

  // Load user role — non-blocking, runs in background
  useEffect(() => {
    if (!user) return;
    const supabase = createClient();
    supabase
      .from('user_profiles')
      .select('role')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.role) setUserRole(data.role);
      });
  }, [user]);

  const TABS = (userRole === 'Administrateur')
    ? TABS_ADMIN
    : TABS_BASE;

  // ─── Skeleton loader while essential data loads ───────────────────────────
  if (loading) {
    return (
      <div className="px-6 lg:px-8 xl:px-10 py-7 max-w-screen-2xl mx-auto animate-pulse">
        {/* Back link skeleton */}
        <div className="h-4 w-32 bg-slate-200 rounded mb-6" />
        {/* Header skeleton */}
        <div className="flex flex-col lg:flex-row gap-6 mb-6">
          <div className="flex-1 space-y-3">
            <div className="h-7 w-2/3 bg-slate-200 rounded-lg" />
            <div className="h-4 w-1/2 bg-slate-100 rounded" />
            <div className="flex gap-2 mt-2">
              <div className="h-6 w-20 bg-slate-100 rounded-full" />
              <div className="h-6 w-24 bg-slate-100 rounded-full" />
            </div>
          </div>
          <div className="w-full lg:w-72 h-48 bg-slate-200 rounded-2xl flex-shrink-0" />
        </div>
        {/* KPI row skeleton */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-100 p-4 space-y-2">
              <div className="h-3 w-16 bg-slate-100 rounded" />
              <div className="h-6 w-20 bg-slate-200 rounded" />
            </div>
          ))}
        </div>
        {/* Tabs skeleton */}
        <div className="flex gap-2 mb-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-9 w-24 bg-slate-100 rounded-xl" />
          ))}
        </div>
        {/* Content skeleton */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-4">
          <div className="h-4 w-full bg-slate-100 rounded" />
          <div className="h-4 w-5/6 bg-slate-100 rounded" />
          <div className="h-4 w-4/6 bg-slate-100 rounded" />
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="flex flex-col items-center justify-center py-32 gap-4">
        <p className="text-slate-500 text-lg">Local introuvable</p>
        <Link href="/liste-des-locaux" className="btn-primary">Retour à la liste</Link>
      </div>
    );
  }

  // ─── Mapped property object ───────────────────────────────────────────────

  const p = {
    id: property.id,
    ref: property.ref,
    titre: property.title,
    statut: property.status,
    priorite: property.priorite || 'Normale',
    source: property.source || '',
    broker: property.broker || '',
    completude: Number(property.completude) || 0,
    ville: property.city || '',
    quartier: property.quartier || '',
    adresse: property.address || '',
    zoneCommerciale: property.zone_commerciale || '',
    lat: Number(property.lat) || 0,
    lng: Number(property.lng) || 0,
    surfaceTotal: Number(property.surface_total) || 0,
    surfaceRDC: Number(property.surface_rdc) || 0,
    surfaceMezzanine: Number(property.surface_mezzanine) || 0,
    surfaceSousSol: Number(property.surface_sous_sol) || 0,
    surfaceEtage: Number(property.surface_etage) || 0,
    surfaceTerrasse: Number(property.surface_terrasse) || 0,
    facade: Number(property.facade) || 0,
    lineaireVitrine: Number(property.lineaire_vitrine) || 0,
    nbVitrines: Number(property.nb_vitrines) || 0,
    proprietaire: property.proprietaire || '',
    typeDetention: property.type_detention || '',
    titreFoncier: property.titre_foncier || '',
    usageAutorise: property.usage_autorise || '',
    loyer: Number(property.loyer) || 0,
    droitEntree: Number(property.droit_entree) || 0,
    pasDePorte: Number(property.pas_de_porte) || 0,
    depotGarantie: Number(property.depot_garantie) || 0,
    disponibilite: property.disponibilite || 'Inconnue',
    exclusivite: Boolean(property.exclusivite),
    hauteurPlafond: Number(property.hauteur_plafond) || 0,
    puissanceElectrique: property.puissance_electrique || '',
    extraction: Boolean(property.extraction),
    climatisation: Boolean(property.climatisation),
    accessibilite: Boolean(property.accessibilite),
    parking: property.parking || '',
    livraison: Boolean(property.livraison),
    etatLocal: property.etat_local || '',
    travauxNecessaires: property.travaux_necessaires || '',
    fluxPieton: property.flux_pieton || '',
    visibiliteEnseigne: property.visibilite_enseigne || '',
    voisinage: property.voisinage || '',
    proximiteTransports: property.proximite_transports || '',
    commentairesPublics: property.commentaires_publics || '',
    nbVisites: Number(property.nb_visites) || 0,
    bestMatchScore: Number(property.best_match_score) || 0,
    bestMatchTenant: property.best_match_tenant || '—',
    updatedAt: property.updated_at || '',
    createdAt: property.created_at ? new Date(property.created_at).toLocaleString('fr-FR') : '—',
    typeLocal: property.type_local || '',
    offre: property.offre || '',
  };

  // ─── Computed matching ────────────────────────────────────────────────────

  const matchedEnseignes = enseignes
    .map(e => {
      const breakdown = computeEnrichedMatchScore(property, e);
      return { ...e, matchScore: breakdown.scoreGlobal, scoreBreakdown: breakdown };
    })
    .filter(e => e.matchScore >= 50)
    .sort((a, b) => b.matchScore - a.matchScore)
    .slice(0, 6);

  // ─── Completeness score ───────────────────────────────────────────────────

  const completenessItems = [
    { label: 'Photos', done: photos.length > 0 },
    { label: 'Surface totale', done: p.surfaceTotal > 0 },
    { label: 'Loyer', done: p.loyer > 0 },
    { label: 'Adresse', done: val(p.adresse) },
    { label: 'Façade', done: p.facade > 0 },
    { label: 'Disponibilité', done: val(p.disponibilite) && p.disponibilite !== 'Inconnue' },
    { label: 'Documents', done: documents.length > 0 },
    { label: 'Géolocalisation', done: p.lat !== 0 && p.lng !== 0 },
    { label: 'Propriétaire', done: val(p.proprietaire) },
    { label: 'État du local', done: val(p.etatLocal) },
  ];
  const computedCompletude = Math.round((completenessItems.filter(i => i.done).length / completenessItems.length) * 100);

  // ─── Surface details ──────────────────────────────────────────────────────

  const surfaceDetails = [
    { label: 'RDC', value: p.surfaceRDC },
    { label: 'Mezzanine', value: p.surfaceMezzanine },
    { label: 'Sous-sol', value: p.surfaceSousSol },
    { label: 'Étage', value: p.surfaceEtage },
    { label: 'Terrasse', value: p.surfaceTerrasse },
  ].filter(s => s.value > 0);

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handlePlanifierVisite = async () => {
    if (!visiteForm.enseigne || !visiteForm.date) {
      toast.error('Renseignez au moins l\'enseigne et la date');
      return;
    }
    const result = await visitesService.create({
      local: p.titre,
      localRef: p.ref,
      enseigne: visiteForm.enseigne,
      contact: visiteForm.contact,
      date: visiteForm.date,
      heure: visiteForm.heure,
      broker: p.broker,
      note: visiteForm.note,
      priorite: 'Normale',
    });
    if (result) {
      setVisites(prev => [result, ...prev]);
      setShowVisiteModal(false);
      setVisiteForm({ enseigne: '', contact: '', date: '', heure: '10:00', note: '' });
      toast.success('Visite planifiée avec succès !');
    } else {
      toast.error('Erreur lors de la planification');
    }
  };

  const handleUploadDoc = async () => {
    if (!uploadFile) { toast.error('Sélectionnez un fichier'); return; }
    setUploading(true);
    const result = await documentsService.create(
      { type: uploadType, local: p.titre, localRef: p.ref },
      uploadFile
    );
    setUploading(false);
    if (result) {
      setDocuments(prev => [result, ...prev]);
      setShowUploadModal(false);
      setUploadFile(null);
      toast.success(`"${uploadFile.name}" uploadé avec succès !`);
    } else {
      toast.error('Erreur lors de l\'upload');
    }
  };

  const openPhoto = (index: number) => {
    setGalleryIndex(index);
    setShowGallery(true);
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="px-6 lg:px-8 xl:px-10 py-7 max-w-screen-2xl mx-auto">
      {/* Photo Gallery Modal */}
      {showGallery && photos.length > 0 && (
        <PhotoGalleryModal photos={photos} onClose={() => setShowGallery(false)} initialIndex={galleryIndex} />
      )}

      {/* Contact Broker Modal */}
      {showContactBrokerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowContactBrokerModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-4 mb-5">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Contacter le broker</h3>
                <p className="text-sm text-slate-500">Responsable de ce local</p>
              </div>
              <button onClick={() => setShowContactBrokerModal(false)} className="ml-auto p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="bg-slate-50 rounded-xl p-4 mb-5 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-700 flex-shrink-0">
                  {p.broker.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="text-sm font-600 text-slate-900">{p.broker}</p>
                  <p className="text-xs text-blue-600">Broker assigné</p>
                </div>
              </div>
              {p.broker && (
                <>
                  <div className="flex items-center gap-2.5 text-sm text-slate-600">
                    <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-slate-500">Contactez via la plateforme</span>
                  </div>
                  <div className="flex items-center gap-2.5 text-sm text-slate-600">
                    <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16a2 2 0 002 2h14a2 2 0 002-2v-1m-4 0h4" />
                    </svg>
                    <span>Réf. local : <strong>{p.ref}</strong></span>
                  </div>
                </>
              )}
            </div>
            <div className="space-y-2">
              <button
                onClick={() => {
                  toast.success(`Message envoyé au broker ${p.broker || ''}`);
                  setShowContactBrokerModal(false);
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                Envoyer un message
              </button>
              <button
                onClick={() => {
                  setShowContactBrokerModal(false);
                  setShowVisiteModal(true);
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-slate-200 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Planifier une visite
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Breadcrumb + actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Link href="/liste-des-locaux" className="hover:text-primary-700 transition-colors">Locaux commerciaux</Link>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-slate-900 font-600">{p.ref}</span>
        </div>
        <div className="flex items-center gap-2.5">
          <button onClick={() => toast.success('Lien copié !')} className="btn-secondary text-xs px-3 py-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            Partager
          </button>
          <button
            onClick={async () => {
              const pdfData = {
                ...p,
                typeLocal: property?.type_local || '',
                offre: property?.offre || '',
                type: property?.type || '',
              };
              toast.info('Génération du PDF en cours...');
              try {
                await generatePDFTeaser(pdfData, photos);
                toast.success('PDF généré avec succès !');
              } catch (err) {
                console.error('PDF error:', err);
                toast.error('Erreur lors de la génération du PDF');
              }
            }}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-600 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors shadow-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21V5a2 2 0 00-2-2H3a2 2 0 00-2 2v16a2 2 0 002 2h14a2 2 0 002-2v-1m-4 0h4" />
            </svg>
            Générer PDF
          </button>
          <Link href={`/locaux/${p.id}/modifier`} className="btn-secondary text-xs px-3 py-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            Modifier
          </Link>
          <button onClick={() => setShowVisiteModal(true)} className="btn-primary text-xs px-3 py-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            + Planifier
          </button>
        </div>
      </div>

      {/* ─── HERO ─────────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-6">
        <div className="grid grid-cols-1 lg:grid-cols-5">

          {/* Photos block */}
          <div className="lg:col-span-3 relative bg-slate-100">
            {photos.length > 0 ? (
              <div className="relative">
                {/* Main photo grid */}
                <div className={`grid gap-1 h-80 ${photos.length >= 3 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  {/* Primary large photo */}
                  <div
                    className={`relative overflow-hidden cursor-pointer group ${photos.length >= 3 ? 'row-span-2' : ''}`}
                    onClick={() => openPhoto(0)}
                  >
                    <img
                      src={photos[0]}
                      alt={`Vue principale de ${p.titre}`}
                      loading="eager"
                      decoding="async"
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                  </div>
                  {/* Secondary photos */}
                  {photos.length >= 2 && (
                    <div className="relative overflow-hidden cursor-pointer group" onClick={() => openPhoto(1)}>
                      <img src={photos[1]} alt={`Photo 2 de ${p.titre}`} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                    </div>
                  )}
                  {photos.length >= 3 && (
                    <div className="relative overflow-hidden cursor-pointer group" onClick={() => openPhoto(2)}>
                      <img src={photos[2]} alt={`Photo 3 de ${p.titre}`} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                      {photos.length > 3 && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <span className="text-white font-700 text-xl">+{photos.length - 3}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {/* "See all photos" button */}
                <button
                  onClick={() => openPhoto(0)}
                  className="absolute bottom-3 right-3 flex items-center gap-2 px-4 py-2 bg-white/95 backdrop-blur-sm text-slate-800 text-xs font-700 rounded-xl shadow-lg hover:bg-white transition-all border border-slate-200"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6 0h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Voir les {photos.length} photo{photos.length > 1 ? 's' : ''}
                </button>
              </div>
            ) : (
              <div className="h-80 flex flex-col items-center justify-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-slate-200 flex items-center justify-center">
                  <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1m-1 4h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="text-center">
                  <p className="text-sm font-600 text-slate-500">Aucune photo disponible</p>
                  <p className="text-xs text-slate-400 mt-1">Ajoutez des photos pour valoriser ce local</p>
                </div>
                <Link href={`/locaux/${p.id}/modifier`} className="btn-secondary text-xs px-4 py-2">
                  Ajouter des photos
                </Link>
              </div>
            )}
          </div>

          {/* Key info panel */}
          <div className="lg:col-span-2 p-6 flex flex-col justify-between">
            <div>
              {/* Title + status */}
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-600 text-slate-400 font-mono tracking-wider">{p.ref}</span>
                  <h1 className="text-xl font-700 text-slate-900 mt-0.5 leading-tight">{p.titre}</h1>
                </div>
                <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                  <span className={`status-badge border ${statusColors[p.statut] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                    {p.statut}
                  </span>
                  {p.offre && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-600 bg-amber-50 text-amber-700 border border-amber-200">
                      {p.offre}
                    </span>
                  )}
                  {p.typeLocal && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-600 bg-violet-50 text-violet-700 border border-violet-200">
                      {p.typeLocal}
                    </span>
                  )}
                </div>
              </div>

              {/* Location */}
              <p className="text-sm text-slate-500 flex items-center gap-1.5 mb-5">
                <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1118 0z"/><circle cx="12" cy="10" r="3"/>
                </svg>
                {[p.adresse, p.quartier, p.ville].filter(Boolean).join(', ')}
              </p>

              {/* Key metrics — only show if value > 0 */}
              <div className="grid grid-cols-2 gap-3 mb-5">
                {p.surfaceTotal > 0 && (
                  <div className="bg-gradient-to-br from-primary-50 to-primary-100/50 rounded-xl p-3.5 border border-primary-100">
                    <p className="text-xs text-primary-600 font-500 mb-0.5">Surface totale</p>
                    <p className="text-2xl font-800 text-primary-800 leading-none">{p.surfaceTotal.toLocaleString('fr-FR')}</p>
                    <p className="text-xs text-primary-600 mt-0.5">m²</p>
                  </div>
                )}
                {p.loyer > 0 ? (
                  <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-100">
                    <p className="text-xs text-slate-500 mb-0.5">Loyer / mois</p>
                    <p className="text-lg font-700 text-slate-900 leading-none">{p.loyer.toLocaleString('fr-FR')}</p>
                    <p className="text-xs text-slate-400 mt-0.5">MAD</p>
                  </div>
                ) : (
                  <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-100">
                    <p className="text-xs text-slate-500 mb-0.5">Loyer / mois</p>
                    <p className="text-lg font-700 text-slate-900 leading-none">Sur demande</p>
                    <p className="text-xs text-slate-400 mt-0.5">MAD</p>
                  </div>
                )}
                {p.facade > 0 && (
                  <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-100">
                    <p className="text-xs text-slate-500 mb-0.5">Façade</p>
                    <p className="text-lg font-700 text-slate-900 leading-none">{p.facade}</p>
                    <p className="text-xs text-slate-400 mt-0.5">mètres linéaires</p>
                  </div>
                )}
                {val(p.disponibilite) && (
                  <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-100">
                    <p className="text-xs text-slate-500 mb-0.5">Disponibilité</p>
                    <p className="text-sm font-700 text-emerald-600 leading-tight">{p.disponibilite}</p>
                  </div>
                )}
              </div>

              {/* Best match score */}
              {p.bestMatchScore > 0 && p.bestMatchTenant !== '—' && (
                <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-xl border border-amber-100 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600 font-700 text-sm flex-shrink-0">
                    <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1m-1 4h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-700 text-amber-700 flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1m-1 4h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                      </svg>
                      Meilleur match
                    </p>
                    <p className="text-sm font-700 text-amber-900 truncate">{p.bestMatchTenant}</p>
                  </div>
                  <span className="text-lg font-800 text-amber-700 flex-shrink-0">{p.bestMatchScore}%</span>
                </div>
              )}
            </div>

            {/* Completeness */}
            <div className="pt-4 border-t border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-600 text-slate-500">Score du local</span>
                <span className={`text-xs font-700 ${computedCompletude >= 80 ? 'text-emerald-600' : computedCompletude >= 50 ? 'text-amber-600' : 'text-red-500'}`}>
                  {computedCompletude}%
                </span>
              </div>
              <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${computedCompletude >= 80 ? 'bg-emerald-500' : computedCompletude >= 50 ? 'bg-amber-400' : 'bg-red-400'}`}
                  style={{ width: `${computedCompletude}%` }}
                />
              </div>
              <div className="flex items-center justify-between mt-2">
                {val(p.broker) && <span className="text-xs text-slate-400">Broker: {p.broker}</span>}
                {val(p.updatedAt) && <span className="text-xs text-slate-400">Mis à jour: {p.updatedAt}</span>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── TABS ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl p-1 mb-6 w-fit shadow-sm overflow-x-auto">
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-600 rounded-lg transition-all duration-150 whitespace-nowrap ${
              activeTab === tab ? 'bg-primary-700 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            {tab}
            {tab === 'Visites' && visites.length > 0 && (
              <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${activeTab === tab ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'}`}>
                {visites.length}
              </span>
            )}
            {tab === 'Documents' && documents.length > 0 && (
              <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${activeTab === tab ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'}`}>
                {documents.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ─── TAB: APERÇU ──────────────────────────────────────────────────── */}
      {activeTab === 'Aperçu' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 space-y-5">

            {/* Surfaces — only if total > 0 */}
            {p.surfaceTotal > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                <h3 className="text-sm font-700 text-slate-900 mb-4 flex items-center gap-2">
                  <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5m5 5l5-5m5 5V12a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2h14a2 2 0 002-2v-1m-4 0h4" />
                  </svg>
                  Surfaces
                </h3>
                {/* Total — always prominent */}
                <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-primary-50 to-primary-100/30 rounded-xl border border-primary-100 mb-4">
                  <div>
                    <p className="text-xs text-primary-600 font-500">Surface totale</p>
                    <p className="text-3xl font-800 text-primary-800">{p.surfaceTotal.toLocaleString('fr-FR')} <span className="text-lg font-600">m²</span></p>
                  </div>
                  {p.loyer > 0 && p.surfaceTotal > 0 && (
                    <div className="ml-auto text-right">
                      <p className="text-xs text-slate-500">Loyer / m²</p>
                      <p className="text-lg font-700 text-slate-700">{Math.round(p.loyer / p.surfaceTotal)} MAD</p>
                    </div>
                  )}
                </div>
                {/* Detail breakdown — only if details exist */}
                {surfaceDetails.length > 0 && (
                  <div>
                    <p className="text-xs font-600 text-slate-400 uppercase tracking-wide mb-3">Détail par niveau</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {surfaceDetails.map(({ label, value }) => (
                        <div key={label} className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                          <p className="text-xs text-slate-400 mb-1">{label}</p>
                          <p className="text-base font-700 text-slate-900">{value.toLocaleString('fr-FR')} <span className="text-xs font-500 text-slate-500">m²</span></p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Caractéristiques — badges */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <h3 className="text-sm font-700 text-slate-900 mb-4 flex items-center gap-2">
                <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
                Caractéristiques
              </h3>
              {/* Boolean features as badges */}
              <div className="flex flex-wrap gap-2 mb-4">
                <FeatureBadge label="Climatisation" active={p.climatisation} />
                <FeatureBadge label="Extraction" active={p.extraction} />
                <FeatureBadge label="Accessibilité PMR" active={p.accessibilite} />
                <FeatureBadge label="Accès livraison" active={p.livraison} />
                {p.exclusivite && <FeatureBadge label="Exclusivité" active={true} />}
              </div>
              {/* Numeric / text specs — only if filled */}
              <div className="grid grid-cols-2 gap-2">
                {p.hauteurPlafond > 0 && (
                  <div className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg">
                    <span className="text-xs text-slate-500">Hauteur plafond</span>
                    <span className="text-xs font-700 text-slate-800">{p.hauteurPlafond} m</span>
                  </div>
                )}
                {val(p.puissanceElectrique) && (
                  <div className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg">
                    <span className="text-xs text-slate-500">Puissance élec.</span>
                    <span className="text-xs font-700 text-slate-800">{p.puissanceElectrique} kVA</span>
                  </div>
                )}
                {val(p.parking) && (
                  <div className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg">
                    <span className="text-xs text-slate-500">Parking</span>
                    <span className="text-xs font-700 text-slate-800">{p.parking} places</span>
                  </div>
                )}
                {val(p.etatLocal) && (
                  <div className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg">
                    <span className="text-xs text-slate-500">État</span>
                    <span className="text-xs font-700 text-slate-800">{p.etatLocal}</span>
                  </div>
                )}
                {p.facade > 0 && (
                  <div className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg">
                    <span className="text-xs text-slate-500">Façade</span>
                    <span className="text-xs font-700 text-slate-800">{p.facade} m</span>
                  </div>
                )}
                {p.nbVitrines > 0 && (
                  <div className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg">
                    <span className="text-xs text-slate-500">Vitrines</span>
                    <span className="text-xs font-700 text-slate-800">{p.nbVitrines}</span>
                  </div>
                )}
              </div>
              {val(p.travauxNecessaires) && (
                <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-100">
                  <p className="text-xs font-600 text-amber-700 mb-1">Travaux nécessaires</p>
                  <p className="text-xs text-amber-800">{p.travauxNecessaires}</p>
                </div>
              )}
            </div>

            {/* Map */}
            {p.lat !== 0 && p.lng !== 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-700 text-slate-900 flex items-center gap-2">
                    <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 002.828 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2h14a2 2 0 002-2v-1m-4 0h4" />
                    </svg>
                    Localisation
                  </h3>
                  <a
                    href={`https://www.openstreetmap.org/?mlat=${p.lat}&mlon=${p.lng}&zoom=17`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-600 text-primary-700 hover:underline flex items-center gap-1"
                  >
                    Ouvrir la carte
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16a2 2 0 002 2h14a2 2 0 002-2v-1m-4 0h4" />
                    </svg>
                  </a>
                </div>
                <div className="rounded-xl overflow-hidden">
                  <MapView
                    markers={[{ id: p.id, lat: p.lat, lng: p.lng, title: p.titre, city: p.ville, loyer: p.loyer, surfaceTotal: p.surfaceTotal, status: p.statut, ref: p.ref }]}
                    center={[p.lat, p.lng]}
                    zoom={16}
                    height="280px"
                  />
                </div>
              </div>
            ) : null}
          </div>

          {/* Right column */}
          <div className="space-y-5">
            {/* Contact Broker Card */}
            {val(p.broker) && (
              <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl border border-blue-200 p-5 shadow-sm">
                <h3 className="text-sm font-700 text-slate-900 mb-3 flex items-center gap-2">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Broker responsable
                </h3>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-700 flex-shrink-0">
                    {p.broker.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-600 text-slate-900">{p.broker}</p>
                    <p className="text-xs text-blue-600">Broker assigné</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowContactBrokerModal(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  Contacter le broker
                </button>
              </div>
            )}

            {/* Conditions commerciales */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <h3 className="text-sm font-700 text-slate-900 mb-4">Conditions commerciales</h3>
              <div className="space-y-2">
                {p.loyer > 0 ? (
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-xs text-slate-500">Loyer mensuel</span>
                    <span className="text-xs font-700 text-primary-700">{p.loyer.toLocaleString('fr-FR')} MAD</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-xs text-slate-500">Loyer mensuel</span>
                    <span className="text-xs font-700 text-slate-800">Sur demande</span>
                  </div>
                )}
                {p.pasDePorte > 0 && (
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-xs text-slate-500">Pas-de-porte</span>
                    <span className="text-xs font-700 text-slate-800">{p.pasDePorte.toLocaleString('fr-FR')} MAD</span>
                  </div>
                )}
                {p.droitEntree > 0 && (
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-xs text-slate-500">Droit d'entrée</span>
                    <span className="text-xs font-700 text-slate-800">{p.droitEntree.toLocaleString('fr-FR')} MAD</span>
                  </div>
                )}
                {p.depotGarantie > 0 && (
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-xs text-slate-500">Dépôt de garantie</span>
                    <span className="text-xs font-700 text-slate-800">{p.depotGarantie.toLocaleString('fr-FR')} MAD</span>
                  </div>
                )}
                {val(p.disponibilite) && (
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-xs text-slate-500">Disponibilité</span>
                    <span className="text-xs font-700 text-emerald-600">{p.disponibilite}</span>
                  </div>
                )}
                <div className="flex items-center justify-between py-2">
                  <span className="text-xs text-slate-500">Exclusivité</span>
                  <span className={`text-xs font-700 ${p.exclusivite ? 'text-emerald-600' : 'text-slate-400'}`}>{p.exclusivite ? 'Oui' : 'Non'}</span>
                </div>
              </div>
            </div>

            {/* Environnement — only if any field filled */}
            {(val(p.fluxPieton) || val(p.visibiliteEnseigne) || val(p.voisinage) || val(p.proximiteTransports)) ? (
              <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                <h3 className="text-sm font-700 text-slate-900 mb-4">Environnement</h3>
                <div className="space-y-2">
                  {val(p.fluxPieton) && (
                    <div>
                      <p className="text-xs text-slate-400 mb-0.5">Flux piéton</p>
                      <p className="text-xs font-600 text-slate-800">{p.fluxPieton}</p>
                    </div>
                  )}
                  {val(p.visibiliteEnseigne) && (
                    <div>
                      <p className="text-xs text-slate-400 mb-0.5">Visibilité enseigne</p>
                      <p className="text-xs font-600 text-slate-800">{p.visibiliteEnseigne}</p>
                    </div>
                  )}
                  {val(p.voisinage) && (
                    <div>
                      <p className="text-xs text-slate-400 mb-0.5">Voisinage</p>
                      <p className="text-xs font-600 text-slate-800">{p.voisinage}</p>
                    </div>
                  )}
                  {val(p.proximiteTransports) && (
                    <div>
                      <p className="text-xs text-slate-400 mb-0.5">Transports</p>
                      <p className="text-xs font-600 text-slate-800">{p.proximiteTransports}</p>
                    </div>
                  )}
                </div>
              </div>
            ) : null}

            {/* Juridique — only if any field filled */}
            {(val(p.proprietaire) || val(p.titreFoncier) || val(p.usageAutorise)) ? (
              <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                <h3 className="text-sm font-700 text-slate-900 mb-4">Informations juridiques</h3>
                <div className="space-y-2">
                  {val(p.proprietaire) && (
                    <div className="flex items-center justify-between py-2 border-b border-slate-100">
                      <span className="text-xs text-slate-500">Propriétaire</span>
                      <span className="text-xs font-600 text-slate-800 text-right max-w-[60%]">{p.proprietaire}</span>
                    </div>
                  )}
                  {val(p.typeDetention) && (
                    <div className="flex items-center justify-between py-2 border-b border-slate-100">
                      <span className="text-xs text-slate-500">Type détention</span>
                      <span className="text-xs font-600 text-slate-800">{p.typeDetention}</span>
                    </div>
                  )}
                  {val(p.titreFoncier) && (
                    <div className="flex items-center justify-between py-2 border-b border-slate-100">
                      <span className="text-xs text-slate-500">Titre foncier</span>
                      <span className="text-xs font-600 text-slate-800">{p.titreFoncier}</span>
                    </div>
                  )}
                  {val(p.usageAutorise) && (
                    <div className="flex items-center justify-between py-2">
                      <span className="text-xs text-slate-500">Usage autorisé</span>
                      <span className="text-xs font-600 text-slate-800">{p.usageAutorise}</span>
                    </div>
                  )}
                </div>
              </div>
            ) : null}

            {/* Completeness checklist */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-700 text-slate-900">Score du local</h3>
                <ScoreRing score={computedCompletude} />
              </div>
              <div className="space-y-2">
                {completenessItems.map(item => (
                  <div key={item.label} className="flex items-center gap-2.5">
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${item.done ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                      {item.done ? (
                        <svg className="w-2.5 h-2.5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <div className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                      )}
                    </div>
                    <span className={`text-xs ${item.done ? 'text-slate-700' : 'text-slate-400'}`}>{item.label}</span>
                  </div>
                ))}
              </div>
              {computedCompletude < 80 && (
                <Link href={`/locaux/${p.id}/modifier`} className="mt-4 w-full btn-secondary text-xs py-2 flex items-center justify-center gap-1.5">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-1m-4 0h4" />
                  </svg>
                  Compléter la fiche
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── TAB: MATCHING ────────────────────────────────────────────────── */}
      {activeTab === 'Matching' && (
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-700 text-slate-900">Enseignes compatibles</h2>
              <p className="text-sm text-slate-500 mt-0.5">Score enrichi : champs standards + données extraites par l'IA (mots-clés, surfaces inférée, critères cachés)</p>
            </div>
            <Link href="/matching" className="btn-secondary text-xs px-3 py-2">Moteur de matching →</Link>
          </div>

          {matchedEnseignes.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {matchedEnseignes.map((e) => {
                const surfaceOk = p.surfaceTotal >= e.surfaceMin && p.surfaceTotal <= e.surfaceMax;
                const loyerOk = p.loyer <= e.budgetMax;
                const villeOk = e.villesCibles.some((v: string) => v.toLowerCase() === p.ville.toLowerCase());
                const facadeOk = p.facade >= e.facade;
                const bd = e.scoreBreakdown;
                const hasAiBonus = bd && bd.scoreAiBonus > 0;
                return (
                  <div key={e.id} className={`bg-white rounded-xl border p-5 shadow-sm hover:shadow-md transition-shadow ${hasAiBonus ? 'border-violet-200' : 'border-slate-200'}`}>
                    <div className="flex items-start gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center text-primary-700 font-700 text-sm flex-shrink-0">
                        {e.nom.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-700 text-slate-900 truncate">{e.nom}</p>
                        <p className="text-xs text-slate-400">{e.secteur}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <ScoreRing score={e.matchScore} />
                        {hasAiBonus && (
                          <span className="text-xs font-600 text-violet-600 bg-violet-50 border border-violet-200 rounded-full px-2 py-0.5">
                            +{bd.scoreAiBonus} IA
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Compatibility grid */}
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <MatchBadge label={`Surface (${e.surfaceMin}–${e.surfaceMax} m²)`} compatible={surfaceOk} />
                      <MatchBadge label={`Budget (≤ ${e.budgetMax.toLocaleString('fr-FR')} MAD)`} compatible={loyerOk} />
                      <MatchBadge label={`Ville (${e.villesCibles.join(', ')})`} compatible={villeOk} />
                      <MatchBadge label={`Façade (≥ ${e.facade} m)`} compatible={facadeOk} />
                    </div>
                    {/* AI enrichment details */}
                    {hasAiBonus && (
                      <div className="bg-violet-50 border border-violet-100 rounded-lg p-3 mb-3 space-y-1.5">
                        <p className="text-xs font-700 text-violet-700 flex items-center gap-1">
                          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1m-1 4h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                          </svg>
                          Enrichissement IA (+{bd.scoreAiBonus} pts)
                        </p>
                        {bd.aiInferredSurfaceUsed && (
                          <p className="text-xs text-violet-600">📐 Surface inférée depuis les textes libres</p>
                        )}
                        {bd.aiKeywordsMatched.length > 0 && (
                          <p className="text-xs text-violet-600">🔑 Mots-clés : {bd.aiKeywordsMatched.slice(0, 3).join(', ')}</p>
                        )}
                        {bd.aiHiddenCriteriaMatched.length > 0 && (
                          <p className="text-xs text-violet-600">🔍 Critères cachés : {bd.aiHiddenCriteriaMatched.slice(0, 2).join(' · ')}</p>
                        )}
                      </div>
                    )}
                    <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
                      <button
                        onClick={() => { setVisiteForm(f => ({ ...f, enseigne: e.nom, contact: e.contact })); setShowVisiteModal(true); }}
                        className="btn-primary text-xs px-3 py-1.5 flex-1"
                      >
                        Planifier visite
                      </button>
                      {val(e.contact) && (
                        <span className="text-xs text-slate-400 truncate">{e.contact}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1m-1 4h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-sm font-600 text-slate-500">Aucune enseigne compatible trouvée</p>
              <p className="text-xs text-slate-400 mt-1 mb-4">Complétez la fiche (surface, loyer, ville) pour activer le matching</p>
              <div className="flex items-center gap-3 justify-center">
                <Link href={`/locaux/${p.id}/modifier`} className="btn-secondary text-xs px-4 py-2">Compléter la fiche</Link>
                <Link href="/matching" className="btn-primary text-xs px-4 py-2">Voir le moteur</Link>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── TAB: VISITES ─────────────────────────────────────────────────── */}
      {activeTab === 'Visites' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              {visites.length > 0
                ? `${visites.length} visite${visites.length > 1 ? 's' : ''} enregistrée${visites.length > 1 ? 's' : ''}`
                : 'Aucune visite enregistrée'}
            </p>
            <button onClick={() => setShowVisiteModal(true)} className="btn-primary text-xs px-3 py-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              + Planifier
            </button>
          </div>
          {visites.length > 0 ? (
            <div className="space-y-3">
              {visites.map((v) => (
                <div key={v.id} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-700 text-slate-900 truncate">{v.enseigne}</p>
                    <p className="text-xs text-slate-400">{v.date} {v.heure && `à ${v.heure}`}</p>
                    {v.note && <p className="text-xs text-slate-500 mt-1">{v.note}</p>}
                  </div>
                  <span className={`status-badge border flex-shrink-0 ${statusColors[v.statut] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                    {v.statut}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <p className="text-sm font-600 text-slate-500">Aucune visite planifiée</p>
              <button onClick={() => setShowVisiteModal(true)} className="mt-3 btn-primary text-xs px-4 py-2">
                Planifier une visite
              </button>
            </div>
          )}
        </div>
      )}

      {/* ─── TAB: DOCUMENTS ───────────────────────────────────────────────── */}
      {activeTab === 'Documents' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              {documents.length > 0
                ? `${documents.length} document${documents.length > 1 ? 's' : ''}`
                : 'Aucun document'}
            </p>
            <button onClick={() => setShowUploadModal(true)} className="btn-primary text-xs px-3 py-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
              + Ajouter
            </button>
          </div>
          {documents.length > 0 ? (
            <div className="space-y-3">
              {documents.map((doc) => (
                <div key={doc.id} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm flex items-center gap-4">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${docTypeColors[doc.type] || 'bg-slate-50 text-slate-400'}`}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-700 text-slate-900 truncate">{doc.nom}</p>
                    <p className="text-xs text-slate-400">{doc.type} · {doc.date}</p>
                  </div>
                  <span className={`status-badge border flex-shrink-0 ${statusColors[doc.statut] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                    {doc.statut}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <p className="text-sm font-600 text-slate-500">Aucun document uploadé</p>
              <button onClick={() => setShowUploadModal(true)} className="mt-3 btn-primary text-xs px-4 py-2">
                Ajouter un document
              </button>
            </div>
          )}
        </div>
      )}

      {/* ─── TAB: HISTORIQUE ──────────────────────────────────────────────── */}
      {activeTab === 'Historique' && (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-sm font-600 text-slate-500">Historique des modifications</p>
          <p className="text-xs text-slate-400 mt-1">Créé le {p.createdAt}</p>
        </div>
      )}

      {/* ─── MODAL: VISITE ────────────────────────────────────────────────── */}
      {showVisiteModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowVisiteModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-700 text-slate-900">Planifier une visite</h3>
              <button onClick={() => setShowVisiteModal(false)} className="p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-600 text-slate-700 mb-1.5">Enseigne *</label>
                <input
                  type="text"
                  value={visiteForm.enseigne}
                  onChange={e => setVisiteForm(f => ({ ...f, enseigne: e.target.value }))}
                  className="input-field w-full text-sm"
                  placeholder="Nom de l'enseigne"
                />
              </div>
              <div>
                <label className="block text-xs font-600 text-slate-700 mb-1.5">Contact</label>
                <input
                  type="text"
                  value={visiteForm.contact}
                  onChange={e => setVisiteForm(f => ({ ...f, contact: e.target.value }))}
                  className="input-field w-full text-sm"
                  placeholder="Nom du contact"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-600 text-slate-700 mb-1.5">Date *</label>
                  <input
                    type="date"
                    value={visiteForm.date}
                    onChange={e => setVisiteForm(f => ({ ...f, date: e.target.value }))}
                    className="input-field w-full text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-600 text-slate-700 mb-1.5">Heure</label>
                  <input
                    type="time"
                    value={visiteForm.heure}
                    onChange={e => setVisiteForm(f => ({ ...f, heure: e.target.value }))}
                    className="input-field w-full text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-600 text-slate-700 mb-1.5">Note</label>
                <textarea
                  value={visiteForm.note}
                  onChange={e => setVisiteForm(f => ({ ...f, note: e.target.value }))}
                  className="input-field w-full text-sm resize-none"
                  rows={3}
                  placeholder="Remarques..."
                />
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowVisiteModal(false)} className="btn-secondary flex-1 text-sm py-2.5">Annuler</button>
              <button onClick={handlePlanifierVisite} disabled={uploading} className="btn-primary flex-1 text-sm py-2.5">
                {uploading ? 'Upload...' : 'Uploader'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── MODAL: UPLOAD ────────────────────────────────────────────────── */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowUploadModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-700 text-slate-900">Ajouter un document</h3>
              <button onClick={() => setShowUploadModal(false)} className="p-2 rounded-lg hover:bg-slate-100 transition-colors">
                <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-600 text-slate-700 mb-1.5">Type de document</label>
                <select
                  value={uploadType}
                  onChange={e => setUploadType(e.target.value)}
                  className="input-field w-full text-sm"
                >
                  {Object.keys(docTypeColors).map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-600 text-slate-700 mb-1.5">Fichier *</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={e => setUploadFile(e.target.files?.[0] || null)}
                  className="input-field w-full text-sm"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowUploadModal(false)} className="btn-secondary flex-1 text-sm py-2.5">Annuler</button>
              <button onClick={handleUploadDoc} disabled={uploading} className="btn-primary flex-1 text-sm py-2.5">
                {uploading ? 'Upload...' : 'Uploader'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}